import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import type {ClassRouteName} from "@khan/class-types";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import SideNavHelpLink from "./side-nav-help-link.tsx";

interface Props {
    // Content for the right-hand main column.
    children: React.ReactNode;
    // Content for the left-hand navigation column.
    nav: React.ReactNode;
    // Used for CEDAR event to know which Class Dashboard route the Help was
    // opened on.
    routeName?: ClassRouteName;
    // An optional help URL for the page. If provided, we'll render a sticky
    // "Help with this page" link in the nav column.
    helpUrl?: string | null | undefined;
    // Optional styles to add to the right-hand main column.
    mainStyle?: StyleType;
    navColumnStyle?: StyleType;
    navContentStyle?: StyleType;
}

/**
 * The main container element for the "side nav" page layout.
 *
 * <SideNavPageLayout nav={<ExampleNav />}>
 *     <ExampleHeader>Hello, world!</ExampleHeader>
 *     <ExamplePageContent />
 * </SideNavPageLayout>
 *
 * This is a full-page-ish layout component: it sizes itself to be large on the
 * assumption that the global header and footer are the only other page
 * elements.
 *
 * It wraps the provided content, and attaches the provided navigation content
 * in a left-hand sidebar. Or, on small screens, it vertically stacks the
 * navigation content above the main content.
 *
 * Note that this component is not inherently coupled to the `nav-links.jsx`
 * components, and could be split into a separate package if needed.
 */
function SideNavPageLayout({
    children,
    nav,
    routeName,
    helpUrl,
    mainStyle,
    navColumnStyle,
    navContentStyle,
}: Props): React.ReactElement {
    return (
        <View style={styles.container}>
            {!RequestInfo.khanClassroomEmbedded && (
                <View style={[styles.navColumn, navColumnStyle]}>
                    <View style={[styles.navColumnContent, navContentStyle]}>
                        {nav}
                    </View>
                    {helpUrl && (
                        <View style={styles.navColumnHelpFooter}>
                            <SideNavHelpLink
                                href={helpUrl}
                                routeName={routeName}
                            />
                        </View>
                    )}
                </View>
            )}
            {/* TODO(mdr): Use a <main /> tag. See WB-529. */}
            <View style={[styles.mainColumn, mainStyle]}>{children}</View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        width: "100%",

        // Tablet & desktop view: two-column layout
        [mediaQueries.smOrLarger]: {
            flexDirection: "row",
        },

        // Mobile view: vertical stacking
        [mediaQueries.xsOrSmaller]: {
            flexDirection: "column",
        },
    },

    navColumn: {
        width: 230,
        minBlockSize: "100vh",
        boxSizing: "border-box",
        flex: "0 0 auto",
        background: semanticColor.core.background.base.default,
        borderInlineEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,

        // Mobile view: vertical stacking
        [mediaQueries.xsOrSmaller]: {
            width: "100%",
            minBlockSize: 0,
            borderInlineEnd: 0,

            paddingBlockStart: spacing.small_12,
            paddingBlockEnd: spacing.small_12,
        },

        "@media print": {
            display: "none",
        },
    },

    navColumnContent: {
        flex: "1 1 auto",
        paddingInlineStart: spacing.xSmall_8,
        paddingInlineEnd: spacing.xSmall_8,

        // Tablet & desktop view: two-column layout
        [mediaQueries.smOrLarger]: {
            paddingBlockStart: spacing.small_12,
            paddingBlockEnd: spacing.small_12,
        },
    },

    navColumnHelpFooter: {
        flex: "0 0 auto",
        background: semanticColor.core.background.base.default,
        paddingInlineStart: spacing.small_12,
        paddingInlineEnd: spacing.small_12,

        // Tablet & desktop view: two-column layout
        [mediaQueries.smOrLarger]: {
            position: "sticky",
            bottom: 0,
            borderBlockStart: `1px solid ${semanticColor.core.border.neutral.subtle}`,

            paddingBlockStart: spacing.xSmall_8,
            paddingBlockEnd: spacing.xSmall_8,

            // A little bit of extra space, so that we don't slide the footer
            // *all* the way to the top of the nav bar when we're being sticky
            // on not-tall windows. Not so much a functional change as
            // mitigating an awkward visual edge case!
            marginBlockStart: spacing.large_24,
        },
    },

    mainColumn: RequestInfo.khanClassroomEmbedded
        ? {
              flex: "1",
          }
        : {
              flex: "1",
              background: semanticColor.core.background.base.default,

              // Tablet & desktop view: two-column layout
              [mediaQueries.smOrLarger]: {
                  paddingTop: spacing.medium_16,
                  paddingBottom: spacing.medium_16,
                  paddingLeft: spacing.xLarge_32,
                  paddingRight: spacing.xLarge_32,
              },

              // Mobile view: vertical stacking
              [mediaQueries.xsOrSmaller]: {
                  paddingTop: spacing.small_12,
                  paddingBottom: spacing.small_12,
                  paddingLeft: spacing.medium_16,
                  paddingRight: spacing.medium_16,
              },

              "@media print": {
                  paddingLeft: spacing.large_24,
                  paddingRight: spacing.large_24,
              },
          },
});

export default SideNavPageLayout;
